/**
 * Page-scoped constants — shared across the visionaries-summit components.
 * Kept in TS instead of CSS so we can hand them straight to Tailwind arbitrary
 * values and framer-motion `animate` props.
 */

/**
 * Inter stylistic alt set for the marketing copy. Matches the typography in
 * the Figma file (`ss01`/`ss02`/`ss03` = single-storey g/a + alt 4, `zero` =
 * slashed zero).
 */
export const VS_FONT_FEATURES =
  "'ss03' 1, 'ss01' 1, 'zero' 1, 'ss02' 1" as const;

/** Pink → blue, left-to-right (used for headline highlights). */
export const VS_GRADIENT_PINK_BLUE =
  "linear-gradient(90deg,#fe634d 3%,#fe5960 7%,#fb5170 11%,#f74b81 15%,#f14592 19%,#e942a3 23%,#e040b4 27%,#d53fc3 31%,#ce3cc9 35%,#c739cf 39%,#bf36d5 43%,#b734db 47%,#af32e0 51%,#a62fe6 55%,#9d2dea 59%,#942aef 63%,#8a28f3 67%,#8125f6 71%,#7623f9 75%,#6b20fc 79%,#5f1ffe 83%,#511dff 87%,#411dff 91%,#2e1dff 95%,#001eff 100%)";

/** Blue → pink, left-to-right (used for the "£10,000" highlight). */
export const VS_GRADIENT_BLUE_PINK =
  "linear-gradient(90deg,#001eff 0%,#2e1dff 4%,#411dff 8%,#511dff 12%,#5f1ffe 16%,#6b20fc 20%,#7623f9 24%,#8125f6 28%,#8a28f3 32%,#942aef 36%,#9d2dea 40%,#a62fe6 44%,#af32e0 48%,#b734db 52%,#bf36d5 56%,#c739cf 60%,#ce3cc9 64%,#d53fc3 68%,#e040b4 72%,#e942a3 76%,#f14592 80%,#f74b81 84%,#fb5170 88%,#fe5960 92%,#fe634d 96%)";

/** Big rainbow band used as the active stat fill. */
export const VS_GRADIENT_STAT_ACTIVE =
  "linear-gradient(90deg,#001eff 0%,#411dff 16%,#7623f9 32%,#8a28f3 42%,#b734db 57%,#ce3cc9 68%,#e040b4 77%,#f14592 87%,#fe5960 95%,#fe634d 100%)";

/** Vertical fade used on the "before" stat (white → mid-grey). */
export const VS_GRADIENT_STAT_BELOW = "linear-gradient(to bottom,#fff,#888)";

/** Vertical fade used on the "after" stat (white → light-grey). */
export const VS_GRADIENT_STAT_ABOVE = "linear-gradient(to bottom,#fff,#aaa)";

/** Hero gradient: deep blue (left) → magenta (right). */
export const VS_HERO_GRADIENT =
  "linear-gradient(90.24deg,#001eff 24.5%,#2e1dff 28.2%,#411dff 31.9%,#511dff 35.6%,#5f1ffe 39.3%,#6b20fc 43%,#7623f9 46.7%,#8125f6 50.4%,#8a28f3 54%,#942aef 57.7%,#9d2dea 61.4%,#a62fe6 65.1%,#af32e0 68.8%,#b734db 72.5%,#bf36d5 76.2%,#c739cf 79.9%,#ce3cc9 83.5%,#d53fc3 87.2%,#e040b4 90.9%,#e942a3 94.6%,#f14592 98.3%)";

/**
 * Action bar gradient — sampled from the hero background video (a still
 * frame's column averages): bright blue fading through teal-navy to near
 * black. Used on the top action bar, and anywhere else that should read as
 * part of the same colour story (e.g. the venue Apply banner).
 */
export const VS_ACTION_BAR_GRADIENT =
  "linear-gradient(90deg, #002d6e 0%, #0062a9 25%, #023563 45%, #034e7d 62%, #031b36 85%, #06141b 100%)";

/** Helper: returns inline `style` props for gradient text in one shot. */
export function gradientTextStyle(gradient: string): React.CSSProperties {
  return {
    backgroundImage: gradient,
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    color: "transparent",
  };
}
